import { useEffect, useMemo, useState } from 'react';

import { Link, useLocation } from 'react-router-dom'
// @mui
import { alpha } from '@mui/material/styles';
import { Box, Divider, Typography, Stack, MenuItem, Avatar, } from '@mui/material';
// components
import MenuPopover from '../../../components/MenuPopover';
import { IconButtonAnimate } from '../../../components/animate';

import useAuth from '../../../hooks/useAuth';
import { getSection } from '../../../utils/getSection';

import { getApplicantById } from '../../../api/applicants';
import { getStudentById } from '../../../api/students';
import { getHomePath } from '../../../routes/paths';
import { getStudentProfilePicture } from '../../../api/profile-picture';

export default function AccountPopover() {
  const { logout, whoAmI } = useAuth();
  const { pathname } = useLocation()
  const section = getSection(pathname)
  const [avatarSrc, setAvatarSrc] = useState()

  const [open, setOpen] = useState(null);

  const profile = useMemo(() => whoAmI?.profile ?? {
    lastName: '',
    givenName: 'User',
    email: '',
  }, [whoAmI])

  const APPLY_MENU_OPTIONS = [
    {
      label: 'Profile',
      linkTo: `/${section}/profile`
    }
  ]

  const STUDENT_MENU_OPTIONS = [
    {
      label: 'My Profile',
      linkTo: `/${section}/settings`
    }
  ]

  const MENU_OPTIONS = useMemo(() => ([
    {
      label: 'Home',
      linkTo: getHomePath(pathname),
    },
    ...(section === 'apply' ? APPLY_MENU_OPTIONS : STUDENT_MENU_OPTIONS)
  ]), [getHomePath, pathname, section])

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
  };

  const handleClose = () => {
    setOpen(null);
  };

  useEffect(() => {
    if (whoAmI) {
      const fetchThumbnail = async () => {
        let getFileFunc
        if (whoAmI?.type === 'student') {
          getFileFunc = getStudentProfilePicture
          const res = await getFileFunc(whoAmI?.profile?._id, { size: 'medium' })
          const base64 = Buffer.from(res, 'binary').toString('base64')
          const imgSrc = `data:image/png;base64,${base64}`
          setAvatarSrc(imgSrc)
        }
      }
      fetchThumbnail()
    }
  }, [whoAmI, avatarSrc])

  return (
    <>
      <IconButtonAnimate
        onClick={handleOpen}
        sx={{
          p: 0,
          ...(open && {
            '&:before': {
              zIndex: 1,
              content: "''",
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              position: 'absolute',
              bgcolor: (theme) => alpha(theme.palette.grey[900], 0.8),
            },
          }),
        }}
      >
        {avatarSrc ?
          <Avatar src={avatarSrc} alt={`${profile.givenName} ${profile.lastName}`} /> :
          <Avatar sx={{ bgcolor: 'primary.main', color: 'primary.contrastText' }}>{profile?.lastName ? `${profile?.givenName[0]?.toUpperCase()}${profile?.lastName[0]?.toUpperCase()}` : ''}</Avatar>
        }
      </IconButtonAnimate>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          mt: 1.5,
          ml: 0.75,
          '& .MuiMenuItem-root': {
            typography: 'body2',
            borderRadius: 0.75,
          },
        }}
      >
        <Box sx={{ my: 1.5, px: 2.5 }}>
          <Typography variant="subtitle2" textTransform='capitalize' noWrap>
            {profile ? `${profile.givenName} ${profile.lastName}` : 'User'}
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }} noWrap>
            {profile.email}
          </Typography>
        </Box>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <Stack sx={{ p: 1 }}>
          {MENU_OPTIONS.map((option) => ( 
            <MenuItem key={option.label} component={Link} to={option.linkTo} onClick={handleClose}>
              {option.label}
            </MenuItem>
          ))}
        </Stack>

        <Divider sx={{ borderStyle: 'dashed' }} />

        <MenuItem sx={{ m: 1 }} onClick={logout}>Logout</MenuItem>
      </MenuPopover >
    </>
  );
}